import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import {
    Building2,
    User,
    Phone,
    Mail,
    MapPin,
    Globe,
    Calendar,
    Plus,
    ArrowLeft,
    Pencil,
    MessageSquare,
    FileText,
    TrendingUp,
    Clock,
    Loader2,
    Edit,
} from 'lucide-react';
import { toast } from 'sonner';

const CompanyDetail = () => {
    const { id } = useParams();
    const { api, user } = useAuth();
    const navigate = useNavigate();
    
    const [company, setCompany] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Modals
    const [showContactModal, setShowContactModal] = useState(false);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    
    // Contact form
    const [contactForm, setContactForm] = useState({
        civility: '',
        first_name: '',
        last_name: '',
        job_title: '',
        email: '',
        phone: '',
        mobile: '',
        is_primary: false,
        notes: '',
    });
    
    // Activity form
    const [activityForm, setActivityForm] = useState({
        type: 'note',
        title: '',
        content: '',
    });

    useEffect(() => {
        fetchCompanyData();
    }, [id]);

    const fetchCompanyData = async () => {
        setLoading(true);
        try {
            const [companyRes, contactsRes, opportunitiesRes, activitiesRes] = await Promise.all([
                api.get(`/companies/${id}`),
                api.get(`/contacts?company_id=${id}`),
                api.get(`/opportunities?company_id=${id}`),
                api.get(`/activities?company_id=${id}`),
            ]);
            
            setCompany(companyRes.data);
            setContacts(contactsRes.data);
            setOpportunities(opportunitiesRes.data);
            setActivities(activitiesRes.data);
        } catch (error) {
            console.error('Failed to fetch company data:', error);
            toast.error('Erreur lors du chargement des données');
            navigate('/companies');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateContact = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            await api.post('/contacts', {
                company_id: id,
                ...contactForm,
            });
            toast.success('Contact créé avec succès');
            setShowContactModal(false);
            setContactForm({
                civility: '',
                first_name: '',
                last_name: '',
                job_title: '',
                email: '',
                phone: '',
                mobile: '',
                is_primary: false,
                notes: '',
            });
            fetchCompanyData();
        } catch (error) {
            console.error('Failed to create contact:', error);
            toast.error('Erreur lors de la création du contact');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCreateActivity = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            await api.post('/activities', {
                company_id: id,
                ...activityForm,
            });
            toast.success('Activité enregistrée');
            setShowActivityModal(false);
            setActivityForm({
                type: 'note',
                title: '',
                content: '',
            });
            fetchCompanyData();
        } catch (error) {
            console.error('Failed to create activity:', error);
            toast.error('Erreur lors de l\'enregistrement');
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusBadge = (statuses) => {
        // Handle both array (new) and string (legacy) formats
        const statusList = Array.isArray(statuses) ? statuses : (statuses ? [statuses] : ['prospect']);
        
        const styles = {
            prospect: 'bg-blue-500/10 text-blue-500',
            client: 'bg-green-500/10 text-green-500',
            annonceur_mag_immo: 'bg-purple-500/10 text-purple-500',
            annonceur_place_to_b: 'bg-orange-500/10 text-orange-500',
            annonceur_decideur: 'bg-pink-500/10 text-pink-500',
            inactive: 'bg-gray-500/10 text-gray-500',
        };
        const labels = {
            prospect: 'Prospect',
            client: 'Client',
            annonceur_mag_immo: "Mag' de l'Immo",
            annonceur_place_to_b: 'Place to B',
            annonceur_decideur: 'Décideur',
            inactive: 'Inactif',
        };
        
        return (
            <div className="flex flex-wrap gap-1">
                {statusList.map((status) => (
                    <Badge key={status} className={styles[status] || styles.prospect}>
                        {labels[status] || status}
                    </Badge>
                ))}
            </div>
        );
    };

    const activityTypes = [
        { value: 'call', label: 'Appel' },
        { value: 'email', label: 'Email' },
        { value: 'meeting', label: 'Rendez-vous' },
        { value: 'note', label: 'Note' },
        { value: 'follow_up', label: 'Relance' },
        { value: 'proposal', label: 'Proposition envoyée' },
        { value: 'other', label: 'Autre' },
    ];

    const getActivityIcon = (type) => {
        const icons = {
            call: Phone,
            email: Mail,
            meeting: Calendar,
            note: MessageSquare,
            follow_up: Clock,
            proposal: FileText,
            other: MessageSquare,
        };
        return icons[type] || MessageSquare;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <Skeleton className="h-10 w-10" />
                    <Skeleton className="h-8 w-64" />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Skeleton className="h-96 lg:col-span-2" />
                    <Skeleton className="h-96" />
                </div>
            </div>
        );
    }

    if (!company) return null;

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/companies')}
                    className="rounded-full self-start"
                    data-testid="back-btn"
                >
                    <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="flex-1">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold font-heading">{company.legal_name}</h1>
                            {company.trade_name && (
                                <p className="text-muted-foreground">{company.trade_name}</p>
                            )}
                        </div>
                        {getStatusBadge(company.statuses || company.status)}
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={() => navigate(`/companies?edit=${id}`)}
                        data-testid="edit-company-btn"
                    >
                        <Pencil className="w-4 h-4 mr-2" />
                        Modifier
                    </Button>
                    <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={() => setShowActivityModal(true)}
                        data-testid="add-activity-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Activité
                    </Button>
                    <Button
                        className="rounded-full"
                        onClick={() => navigate(`/pipeline?company=${id}`)}
                        data-testid="create-opportunity-btn"
                    >
                        <TrendingUp className="w-4 h-4 mr-2" />
                        Nouvelle opportunité
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main content */}
                <div className="lg:col-span-2 space-y-6">
                    <Tabs defaultValue="overview" className="w-full">
                        <TabsList className="w-full justify-start">
                            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
                            <TabsTrigger value="contacts">
                                Contacts ({contacts.length})
                            </TabsTrigger>
                            <TabsTrigger value="opportunities">
                                Opportunités ({opportunities.length})
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="overview" className="space-y-4 mt-4">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg font-heading">Informations</CardTitle>
                                </CardHeader>
                                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {company.siret && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">SIRET</p>
                                            <p className="font-medium">{company.siret}</p>
                                        </div>
                                    )}
                                    {company.sector && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Secteur</p>
                                            <p className="font-medium">{company.sector}</p>
                                        </div>
                                    )}
                                    {(company.address_line_1 || company.city) && (
                                        <div className="md:col-span-2">
                                            <p className="text-sm text-muted-foreground">Adresse</p>
                                            <p className="font-medium flex items-center gap-1">
                                                <MapPin className="w-4 h-4" />
                                                {[company.address_line_1, company.postal_code, company.city].filter(Boolean).join(', ')}
                                            </p>
                                        </div>
                                    )}
                                    {company.website && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Site web</p>
                                            <a
                                                href={company.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-medium text-primary flex items-center gap-1 hover:underline"
                                            >
                                                <Globe className="w-4 h-4" />
                                                {company.website}
                                            </a>
                                        </div>
                                    )}
                                    {company.source && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Source</p>
                                            <p className="font-medium">{company.source}</p>
                                        </div>
                                    )}
                                    {company.owner_name && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Commercial référent</p>
                                            <p className="font-medium flex items-center gap-1">
                                                <User className="w-4 h-4" />
                                                {company.owner_name}
                                            </p>
                                        </div>
                                    )}
                                    {company.notes && (
                                        <div className="md:col-span-2">
                                            <p className="text-sm text-muted-foreground">Notes</p>
                                            <p className="font-medium whitespace-pre-wrap">{company.notes}</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="contacts" className="mt-4">
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between">
                                    <CardTitle className="text-lg font-heading">Contacts</CardTitle>
                                    <Button
                                        size="sm"
                                        className="rounded-full"
                                        onClick={() => setShowContactModal(true)}
                                        data-testid="add-contact-btn"
                                    >
                                        <Plus className="w-4 h-4 mr-1" />
                                        Ajouter
                                    </Button>
                                </CardHeader>
                                <CardContent>
                                    {contacts.length === 0 ? (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                            <p>Aucun contact</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {contacts.map((contact) => (
                                                <div
                                                    key={contact.id}
                                                    className="flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors"
                                                    data-testid={`contact-${contact.id}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Avatar>
                                                            <AvatarFallback className="bg-primary/10 text-primary">
                                                                {contact.first_name?.[0]}{contact.last_name?.[0]}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <div>
                                                            <p className="font-medium">
                                                                {contact.civility} {contact.first_name} {contact.last_name}
                                                                {contact.is_primary && (
                                                                    <Badge variant="outline" className="ml-2 text-xs">Principal</Badge>
                                                                )}
                                                            </p>
                                                            {contact.job_title && (
                                                                <p className="text-sm text-muted-foreground">{contact.job_title}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                        {contact.email && (
                                                            <a href={`mailto:${contact.email}`} className="hover:text-primary">
                                                                <Mail className="w-4 h-4" />
                                                            </a>
                                                        )}
                                                        {contact.phone && (
                                                            <a href={`tel:${contact.phone}`} className="hover:text-primary">
                                                                <Phone className="w-4 h-4" />
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="opportunities" className="mt-4">
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between">
                                    <CardTitle className="text-lg font-heading">Opportunités</CardTitle>
                                    <Button
                                        size="sm"
                                        className="rounded-full"
                                        onClick={() => navigate(`/pipeline?company=${id}`)}
                                        data-testid="create-opp-btn"
                                    >
                                        <Plus className="w-4 h-4 mr-1" />
                                        Créer
                                    </Button>
                                </CardHeader>
                                <CardContent>
                                    {opportunities.length === 0 ? (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                            <p>Aucune opportunité</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {opportunities.map((opp) => (
                                                <Link
                                                    key={opp.id}
                                                    to={`/pipeline?opportunity=${opp.id}`}
                                                    className="flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors"
                                                    data-testid={`opportunity-${opp.id}`}
                                                >
                                                    <div>
                                                        <p className="font-medium">{opp.title}</p>
                                                        {opp.estimated_amount && (
                                                            <p className="text-sm text-muted-foreground">
                                                                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(opp.estimated_amount)}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <Badge style={{ backgroundColor: opp.stage_color || '#6B7280' }} className="text-white">
                                                        {opp.stage_label}
                                                    </Badge>
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </div>

                {/* Sidebar - Activity Timeline */}
                <div className="space-y-6">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-lg font-heading">Historique</CardTitle>
                            <Button
                                size="sm"
                                variant="ghost"
                                className="rounded-full"
                                onClick={() => setShowActivityModal(true)}
                            >
                                <Plus className="w-4 h-4" />
                            </Button>
                        </CardHeader>
                        <CardContent>
                            <ScrollArea className="h-[500px] pr-4">
                                {activities.length === 0 ? (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                        <p>Aucune activité</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {activities.map((activity, index) => {
                                            const Icon = getActivityIcon(activity.type);
                                            return (
                                                <div key={activity.id} className="relative pl-6">
                                                    {index !== activities.length - 1 && (
                                                        <div className="absolute left-[11px] top-8 bottom-0 w-0.5 bg-border" />
                                                    )}
                                                    <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                                                        <Icon className="w-3 h-3 text-muted-foreground" />
                                                    </div>
                                                    <div className="pb-4">
                                                        <p className="font-medium text-sm">{activity.title}</p>
                                                        {activity.content && (
                                                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                                                {activity.content}
                                                            </p>
                                                        )}
                                                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                                                            <span>{activity.user_name}</span>
                                                            <span>•</span>
                                                            <span>{formatDate(activity.created_at)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </ScrollArea>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Contact Modal */}
            <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Nouveau contact</DialogTitle>
                        <DialogDescription>
                            Ajoutez un contact pour {company.legal_name}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateContact} className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>Civilité</Label>
                                <Select
                                    value={contactForm.civility}
                                    onValueChange={(v) => setContactForm({ ...contactForm, civility: v })}
                                >
                                    <SelectTrigger className="rounded-lg">
                                        <SelectValue placeholder="--" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="M.">M.</SelectItem>
                                        <SelectItem value="Mme">Mme</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Prénom *</Label>
                                <Input
                                    value={contactForm.first_name}
                                    onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                                    required
                                    className="rounded-lg"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Nom *</Label>
                                <Input
                                    value={contactForm.last_name}
                                    onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                                    required
                                    className="rounded-lg"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Fonction</Label>
                            <Input
                                value={contactForm.job_title}
                                onChange={(e) => setContactForm({ ...contactForm, job_title: e.target.value })}
                                className="rounded-lg"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    value={contactForm.email}
                                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                    className="rounded-lg"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Téléphone</Label>
                                <Input
                                    value={contactForm.phone}
                                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                                    className="rounded-lg"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowContactModal(false)}
                                className="rounded-full"
                            >
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting} className="rounded-full">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Activity Modal */}
            <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Nouvelle activité</DialogTitle>
                        <DialogDescription>
                            Enregistrez une interaction avec {company.legal_name}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateActivity} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Type</Label>
                            <Select
                                value={activityForm.type}
                                onValueChange={(v) => setActivityForm({ ...activityForm, type: v })}
                            >
                                <SelectTrigger className="rounded-lg">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {activityTypes.map((type) => (
                                        <SelectItem key={type.value} value={type.value}>
                                            {type.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Titre *</Label>
                            <Input
                                value={activityForm.title}
                                onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })}
                                required
                                placeholder="Ex: Appel de présentation"
                                className="rounded-lg"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Détails</Label>
                            <Textarea
                                value={activityForm.content}
                                onChange={(e) => setActivityForm({ ...activityForm, content: e.target.value })}
                                rows={4}
                                placeholder="Notes sur l'interaction..."
                                className="rounded-lg"
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowActivityModal(false)}
                                className="rounded-full"
                            >
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting} className="rounded-full">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CompanyDetail;
